import { API_AUTH_BASE_URL } from '@/consts/baseUrls'
import { normalizeEmailAddress, normalizeOptionalEmailAddress } from '@/lib/auth/emailAddress'
import axios, { axiosPkg } from '@/utils/axios'

// Result shape for authentication actions.
type AuthResult = {
    ok: boolean
    error?: string
}

// Result shape for registration (including optional status message).
type RegisterResult = AuthResult & {
    message?: string
}

export const AuthClient = {
    // Delegate credential auth to the server-side session endpoint.
    async loginAsUser(email: string, password: string): Promise<AuthResult> {
        const normalizedEmail = normalizeEmailAddress(email)

        try {
            await axios.post(`${API_AUTH_BASE_URL}/loginUser`, {
                email: normalizedEmail,
                password,
            })

            return { ok: true }
        } catch (error) {
            if (axiosPkg.isAxiosError(error)) {
                return {
                    ok: false,
                    error: error.response?.data?.error ?? 'LOGIN_FAILED',
                }
            }

            return { ok: false, error: 'LOGIN_FAILED' }
        }
    },

    async loginAsSessionInstantUser(username: string, password: string, sessionId: string): Promise<AuthResult> {
        try {
            await axios.post(`${API_AUTH_BASE_URL}/loginSessionInstantUser`, {
                username,
                password,
                sessionId,
            })

            return { ok: true }
        } catch (error) {
            if (axiosPkg.isAxiosError(error)) {
                return {
                    ok: false,
                    error: error.response?.data?.error ?? 'LOGIN_FAILED',
                }
            }

            return { ok: false, error: 'LOGIN_FAILED' }
        }
    },

    // Clear the server-side session cookie.
    async logout() {
        try {
            await axios.post(`${API_AUTH_BASE_URL}/logout`, null, {
                withCredentials: true,
            })
        } catch (_error) {
            // Silent fail for logout
        }
    },

    // Fetch the current session payload for client-side checks.
    async getSession() {
        try {
            const res = await axios.get(`${API_AUTH_BASE_URL}/session`, {
                withCredentials: true,
            })
            return res.data
        } catch (_error) {
            return null
        }
    },

    // Create the user and trigger email verification.
    async register(
        email: string,
        password: string,
        username: string,
        age: string,
        parentEmail?: string,
        transferData?: boolean,
        acceptedTerms?: boolean,
        utm?: Record<string, string>,
    ): Promise<RegisterResult> {
        const normalizedEmail = normalizeEmailAddress(email)
        const normalizedParentEmail = normalizeOptionalEmailAddress(parentEmail)

        try {
            await axios.post(`${API_AUTH_BASE_URL}/register`, {
                email: normalizedEmail,
                password,
                username,
                age,
                parentEmail: normalizedParentEmail,
                transferData,
                acceptedTerms,
                ...(utm ? { utm } : {}),
            })

            return { ok: true }
        } catch (error) {
            if (axiosPkg.isAxiosError(error)) {
                return {
                    ok: false,
                    error: error.response?.data?.error ?? 'Registrierung fehlgeschlagen.',
                }
            }
            return { ok: false, error: 'Registrierung fehlgeschlagen.' }
        }
    },
}
